import React, { useState, useEffect } from "react";
import { Box, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridSelectionModel } from "@mui/x-data-grid";
import { RiExpandUpDownLine } from "react-icons/ri";
import {
  IconButton,
  LinearProgress,
  Button,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import { formatUtcToLocal } from "../../helper/utils";
import useAuth from "../../context/AuthProvider";
import formatPrice from "../../helper/currencyFormatter";
import { StateReportModel } from "../../models/StateReportModel";
import { config } from "../../Constants";
import { AddEditStateReport } from "../AddEdit/AddEditStateReport";
import { Br18Form } from "../AddEdit/Br18Form";
import { br18Data_resp, br9Data_resp } from "../../models/StateReportModel";
import StateReportFileNowDialog from "../Elements/StateReportFileNowDialog";
import { apiClient } from "../../helper/api";
import Swal from "sweetalert2";
import { RemoveRedEyeOutlined } from "@mui/icons-material";
import { StateReportPreviewDialog } from "../Elements/StateReportPreviewDialog";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

interface FilingScheduleData {
  [key: string]: any;
}

interface ScheduleDataByType {
  premiumBearing: FilingScheduleData | null;
  zeroReport: FilingScheduleData | null;
}

interface Iprops {
  itemList: StateReportModel[];
  totalCount: number;
  setFilterModel: any;
  setSortModel: any;
  sortModel?: { field: string; sort: string }[];
  selectedTab: number;
  setSelectedRows: React.Dispatch<
    React.SetStateAction<StateReportModel[] | undefined>
  >;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  _currentPage: number;
  _pageSize: number;
  selectedRows: StateReportModel[] | undefined;
  onRefresh?: () => void;
  states: { stateId: number; stateName: string }[];
  setSelectedTab?: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export const columnWidth = {
  homeState: 70,
  reportName: 190,
  period: 140,
  dueDate: 130,
  isPayment: 190,
  isReport: 180,
  paymentType: 165,
  isWetSignature: 230,
  isNotaryStamp: 235,
  licensee: 270,
  licenseNumber: 180,
  client: 190,
  isMainLicensee: 160,
  isZeroIndividual: 170,
  isZeroEntity: 140,
  status: 60,
  isLargeReport: 150,
  notesToClient: 170,
  filerName: 140,
  taxAmount: 160,
  actions1: 80,
  dateFinalReportSentToClient: 190,
  fileNow: 100,
  edit: 80,
  preview: 80,
};

export const StateReportList: React.FC<Iprops> = ({
  itemList,
  totalCount,
  setFilterModel,
  setSortModel,
  sortModel = [],
  selectedTab,
  setSelectedRows,
  setCurrentPage,
  setPageSize,
  _currentPage,
  _pageSize,
  selectedRows,
  onRefresh,
  states,
  setSelectedTab,
  loading,
  setLoading,
}) => {
  const { user } = useAuth();
  const token = user?.token;
  const [br18Data, setBr18Data] = useState<br18Data_resp>({
    e24GrossPremiumsCharged: "",
    e25AmountOfReturnedPremiums: "",
    a33Attestation: "",
    b33DateInFormat: "",
    d33SpecialBrokerName: "",
    b36BusinessAddress: "",
    b37TelephoneNumber: "",
    b38BrokerLicenseNumber: "",
    b39AuthorizedOfficer: "",
    b40EmailAddress: "",
    b41PreparersName: "",
    b42PreparersTelephone: "",
    b43PreparersEMailAddress: "",
  });
  const [br9Data, setBr9Data] = useState<Array<br9Data_resp>>([]);
  const [openBr18, setOpenBr18] = useState(false);
  // const [loading] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [formData, setFormData] = useState<StateReportModel>();
  const [openFileNowDialog, setOpenFileNowDialog] = useState(false);
  const [selectedRowForFileNow, setSelectedRowForFileNow] =
    useState<StateReportModel | null>(null);
  const [scheduleDataByType, setScheduleDataByType] =
    useState<ScheduleDataByType | null>(null);
  const [fileNowLoadingId, setFileNowLoadingId] = useState<number | string | null>(
    null,
  );
  const [loadingEditData, setLoadingEditData] = useState<number | null>(null); // Track which row is loading
  const [loadingPreviewDataId, setLoadingPreviewDataId] = useState<
    number | null
  >(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const handleEdit = async (event: React.MouseEvent<HTMLButtonElement>, rowData: StateReportModel) => {
    event.preventDefault();
    event.stopPropagation();
    // If stateReportId exists, fetch data from backend
    if (rowData?.stateReportId && rowData.stateReportId > 0) {
      setLoadingEditData(rowData.stateReportId);
      try {
        const response = await apiClient("/StateReport/GetStateReportById", {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportId: rowData.stateReportId,
          }),
        });

        if (response?.status === true && response?.data) {
          setFormData(response.data as StateReportModel);
          setOpenEdit(true);
        } else {
          throw new Error(
            response?.message || "Failed to fetch state report data",
          );
        }
      } catch (error: any) {
        console.error("Error fetching state report data:", error);
        Swal.fire({
          title: "Error",
          text:
            error?.message ||
            "Failed to fetch state report data. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoadingEditData(null);
      }
    } else {
      // If no stateReportId, use row data directly (for new records)
      setFormData(rowData);
      setOpenEdit(true);
    }
  };

  // Handle URL parameters for opening edit dialog from email link
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stateReportIdParam = urlParams.get("stateReportId");
    const openEditParam = urlParams.get("openEdit");

    if (openEditParam === "true" && stateReportIdParam) {
      const stateReportId = parseInt(stateReportIdParam, 10);
      if (!isNaN(stateReportId) && stateReportId > 0) {
        // Set selectedTab to 2 (Completed Queue) by default when opening from URL
        if (setSelectedTab) {
          setSelectedTab(2);
        }
        // Reuse handleEdit function with a minimal object containing stateReportId
        handleEdit(new MouseEvent("click") as unknown as React.MouseEvent<HTMLButtonElement>, { stateReportId } as StateReportModel).then(() => {
          // Clean up the URL parameters after handling
          const newUrl = window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, setSelectedTab]);

  const handleDocIcon = (rowData: StateReportModel) => {
    fetch(
      `${config.url.API_URL}/StateReports/ManualSubmission_MA?partnerId=${rowData.partnersId}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
      },
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((responseJson) => {
        const br9Temp = responseJson.data.bR9.map((item: br9Data_resp) => {
          item.specBkrLicense = (rowData as any).licenseNumber;
          if (item.grossPremium < 0) {
            item.returnPremium = -item.grossPremium;
            item.grossPremium = 0;
          }
          return item;
        });

        const br18Temp = {
          ...responseJson.data.bR18,
          b38BrokerLicenseNumber: (rowData as any).licenseNumber,
          e25AmountOfReturnedPremiums: br9Temp.reduce(
            (sum: number, obj: any) => sum + obj.returnPremium,
            0,
          ),
          e24GrossPremiumsCharged: br9Temp.reduce(
            (sum: number, obj: any) => sum + obj.grossPremium,
            0,
          ),
        };

        setBr18Data(br18Temp);
        setBr9Data(br9Temp);
        setOpenBr18(true);
      })
      .catch((error) => {
        console.log(error);
        setOpenBr18(false);
      });
  };

  const handleClose = () => {
    setOpenEdit(false);
    setFormData(undefined); // Clear form data to prevent carryover
  };

  const handleBrClose = () => {
    setOpenBr18(false);
  };

  const handleSubmit = () => {
    setOpenEdit(false);
    setFormData(undefined); // Clear form data to prevent carryover
    // Refresh the data after successful submission (only current queue)
    if (onRefresh) {
      onRefresh();
    }
  };

  const onRowsSelectionHandler = (ids: GridSelectionModel) => {
    const selectedIDs = new Set(ids);
    const selectedRowData = itemList.filter((row) =>
      selectedIDs.has(row?.rowNum ?? ""),
    );
    setSelectedRows(selectedRowData);
  };

  const createColumn = (
    field: string,
    headerName: string,
    minWidth: number,
    renderCell?: (params: any) => React.ReactNode,
    headerAlign?: "left" | "right",
    align?: "left" | "right",
  ): GridColDef => ({
    field,
    headerName,
    type: "string",
    headerAlign: headerAlign,
    hideSortIcons: true,
    renderHeader: (params) => {
      const sortItem = sortModel?.find(
          (item: any) => item.field === field
        );
    
        const isSortedAsc = sortItem?.sort === 'asc';
        const isSortedDesc = sortItem?.sort === 'desc'
        return (
      <div>
          <span className="table-card-title" >{params.colDef.headerName}</span>
          {isSortedAsc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span> : isSortedDesc ? <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span> : <span className=""><RiExpandUpDownLine /></span>}
      </div>
    )},
    align: align,
    minWidth,
    ...(renderCell && { renderCell }),
  });

  const createDateColumn = (
    field: string,
    headerName: string,
    minWidth: number,
  ): GridColDef =>
    createColumn(field, headerName, minWidth, (params) => (
      <div>
        {params.row[field] !== null && params.row[field] !== ""
          ? formatUtcToLocal(params.row[field], "MM/DD/YYYY")
          : ""}
      </div>
    ));

  const createBooleanColumn = (
    field: string,
    headerName: string,
    minWidth: number,
  ): GridColDef =>
    createColumn(field, headerName, minWidth, (params) => {
      const value = params.value;
      if (value === null || value === undefined || value === "") {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              color: "#999",
              fontStyle: "italic",
            }}
          >
            N/A
          </Box>
        );
      }

      // Handle various boolean representations from API
      const isTrue =
        String(value).toLowerCase() === "true" || value === true || value === 1;

      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          {isTrue ? "Yes" : "No"}
        </Box>
      );
    });

  const createCurrencyColumn = (
    field: string,
    headerName: string,
    minWidth: number,
  ): GridColDef =>
    createColumn(field, headerName, minWidth, (params) => {
      const value = params.value;
      if (value === null || value === undefined || value === "") {
        return (
          <Box display="flex" marginLeft="auto">
            $0.00
          </Box>
        );
      }
      // Convert number to string for formatPrice function
      const stringValue =
        typeof value === "number" ? value.toString() : value || "0";
      return (
        <Box display="flex" marginLeft="auto" >
          {formatPrice(stringValue)}
        </Box>
      );
    }, "right", "right");

  const handleFileNow = async (rowData: StateReportModel) => {
    const loadingId = rowData?.rowNum ?? rowData?.stateReportId ?? null;
    setFileNowLoadingId(loadingId);
    try {
      const response = await apiClient(`/StateReport/getStateReportFilingScheduleData`, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify({
          reportName: rowData.name || "",
          scheduleId: rowData.scheduleId || 0,
          typeOfReport: "",
        }),
      });

      const fetchedData = response?.data;
      const mappedScheduleDataByType: ScheduleDataByType = {
        premiumBearing: fetchedData?.premiumBearing ?? null,
        zeroReport: fetchedData?.zeroReport ?? null,
      };

      setScheduleDataByType(mappedScheduleDataByType);
      setSelectedRowForFileNow(rowData);
      setOpenFileNowDialog(true);
    } catch (error: any) {
      console.error("Error fetching filing schedule data:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch filing schedule data. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setFileNowLoadingId(null);
    }
  };

  const handlePreview = async (event: React.MouseEvent<HTMLButtonElement>, rowData: StateReportModel) => {
    // If stateReportId exists, fetch data from backend
    event.preventDefault();
    event.stopPropagation();
    if (rowData?.stateReportId && rowData.stateReportId > 0) {
      setLoadingPreviewDataId(rowData.stateReportId);
      try {
        const response = await apiClient("/StateReport/GetStateReportById", {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportId: rowData.stateReportId,
          }),
        });

        if (response?.status === true && response?.data) {
          setSelectedRowForFileNow(response.data as StateReportModel);
          setPreviewDialogOpen(true);
        } else {
          throw new Error(
            response?.message || "Failed to fetch state report data",
          );
        }
      } catch (error: any) {
        console.error("Error fetching state report data:", error);
        Swal.fire({
          title: "Error",
          text:
            error?.message ||
            "Failed to fetch state report data. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoadingPreviewDataId(null);
      }
    } else {
      // If no stateReportId, use row data directly (for new records)
      setSelectedRowForFileNow(rowData);
      setPreviewDialogOpen(true);
    }
  };

  const handleFileNowDialogClose = () => {
    setOpenFileNowDialog(false);
    setSelectedRowForFileNow(null);
    setScheduleDataByType(null);
  };

  const handleFileNowDialogSubmit = (data: any) => {
    // Handle the File Now submission here
    console.log("File Now submitted:", data);
    handleFileNowDialogClose();
    setSelectedRows([]);
    if (onRefresh) {
      onRefresh();
    }
  };

  const createFileNowColumn = (): GridColDef => ({
    field: "fileNow",
    headerName: "",
    hideSortIcons: true,
    minWidth: columnWidth["fileNow"],
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const rowLoadingId = params.row?.rowNum ?? params.row?.stateReportId ?? null;
      const isLoading = fileNowLoadingId === rowLoadingId;
      return (
        <Button
          variant="contained"
          onClick={() => handleFileNow(params.row)}
          disabled={isLoading}
          sx={{
            backgroundColor: "#4CAF50",
            color: "white",
            "&:hover": {
              backgroundColor: "#45a049",
            },
            borderRadius: "4px",
            padding: "6px 16px",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          {isLoading ? <CircularProgress size={18} color="inherit" /> : "File Now"}
        </Button>
      );
    },
  });

  const createEditColumn = (): GridColDef => ({
    field: "edit",
    flex: 0.4,
    headerName: "",
    hideSortIcons: true,
    minWidth: columnWidth["edit"],
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      // Don't show edit icon if isDoneByClient is true
      const isDoneByClient =
        params.row.isDoneByClient === true ||
        String(params.row.isDoneByClient) === "true";
      if (isDoneByClient) {
        return null;
      }

      const isLoading = loadingEditData === params.row.stateReportId;
      return (
        <IconButton
          color="primary"
          onClick={(event) => handleEdit(event, params.row)}
          disabled={isLoading}
          sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
        >
          {isLoading ? (
            <Tooltip
              title="Edit"
              placement="top"
              arrow
            >
              <CircularProgress size={20} color="primary" />
            </Tooltip>
          ) : (
            <Tooltip
              title="Edit"
              placement="top"
              arrow
            >
              <EditOutlinedIcon fontSize="medium" color="primary" />
            </Tooltip>
          )}
        </IconButton>
      );
    },
  });

  const createPreviewColumn = (): GridColDef => ({
    field: "preview",
    flex: 0.4,
    headerName: "",
    hideSortIcons: true,
    minWidth: columnWidth["preview"],
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const isLoading = loadingPreviewDataId === params.row.stateReportId;
      return (
        <IconButton
          color="primary"
          onClick={(event) => handlePreview(event, params.row)}
          disabled={isLoading}
          sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
        >
          {isLoading ? (
            <Tooltip
              title="Preview"
              placement="top"
              arrow
            >
            <CircularProgress size={20} color="primary" />
            </Tooltip>
          ) : (
            <Tooltip
              title="Preview"
              placement="top"
              arrow
            >
              <RemoveRedEyeOutlined fontSize="small" sx={{ color: "gray" }} />
            </Tooltip>
          )}
        </IconButton>
      );
    },
  });

  const createActionsColumn = (): GridColDef => ({
    field: "stateRule",
    flex: 0.4,
    headerName: "Edit",
    hideSortIcons: true,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    minWidth: columnWidth["actions1"],
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <div>
        <IconButton color="primary" sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }} onClick={(event) => handleEdit(event, params.row)}>
          <Tooltip
            title="Edit"
            placement="top"
            arrow
          >
            <EditOutlinedIcon fontSize="medium" color="primary" />
          </Tooltip>
        </IconButton>
        {params.row.stateName === "MA" && (
          <IconButton
          sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
          color="primary" onClick={() => handleDocIcon(params.row)}>
            <Tooltip
              title="View Document"
              placement="top"
              arrow
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M9.5 0a.5.5 0 0 1 .5.5.5.5 0 0 0 .5.5.5.5 0 0 1 .5.5V2a.5.5 0 0 1-.5.5h-5A.5.5 0 0 1 5 2v-.5a.5.5 0 0 1 .5-.5.5.5 0 0 0 .5-.5.5.5 0 0 1 .5-.5z" />
                <path d="M3 2.5a.5.5 0 0 1 .5-.5H4a.5.5 0 0 0 0-1h-.5A1.5 1.5 0 0 0 2 2.5v12A1.5 1.5 0 0 0 3.5 16h9a1.5 1.5 0 0 0 1.5-1.5v-12A1.5 1.5 0 0 0 12.5 1H12a.5.5 0 0 0 0 1h.5a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5z" />
                <path d="M10.854 7.854a.5.5 0 0 0-.708-.708L7.5 9.793 6.354 8.646a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0z" />
              </svg>
            </Tooltip>
          </IconButton>
        )}
      </div>
    ),
  });

  const getColumns = (): GridColDef[] => {
    const baseColumns = [
      createColumn("stateName", "State", columnWidth["homeState"]),
      createColumn("name", "Report Name", columnWidth["reportName"]),
      createColumn("period", "Period", columnWidth["period"]),
      createDateColumn("dueDate", "Due Date", columnWidth["dueDate"]),
      createBooleanColumn(
        "payment",
        "Payment Required",
        columnWidth["isPayment"],
      ),
      createBooleanColumn("report", "Report Required", columnWidth["isReport"]),
      createColumn("paymentType", "Payment Type", columnWidth["paymentType"]),
      createBooleanColumn(
        "wetSignature",
        "Wet Signature Required",
        columnWidth["isWetSignature"],
      ),
      createBooleanColumn(
        "notaryStamp",
        "Notary Stamp Required",
        columnWidth["isNotaryStamp"],
      ),
      createColumn("licensee", "Licensee", columnWidth["licensee"]),
      createColumn(
        "licenseNumber",
        "License Number",
        columnWidth["licenseNumber"],
      ),
      createColumn("client", "Client", columnWidth["client"]),
      createBooleanColumn(
        "mainLicensee",
        "Main Licensee",
        columnWidth["isMainLicensee"],
      ),
      createBooleanColumn(
        "zeroIndividual",
        "Zero Individual",
        columnWidth["isZeroIndividual"],
      ),
      createBooleanColumn(
        "zeroEntity",
        "Zero Entity",
        columnWidth["isZeroEntity"],
      ),
      createColumn("status", "Status", columnWidth["status"]),
      createBooleanColumn(
        "largeReport",
        "Large Report",
        columnWidth["isLargeReport"],
      ),
      createColumn("notes", "Notes To Client", columnWidth["notesToClient"]),
      createColumn("filerName", "Filer Name", columnWidth["filerName"]),
      createCurrencyColumn(
        "taxAmount",
        "Tax Reported",
        columnWidth["taxAmount"],
      ),
    ];

    if (selectedTab === 1) {
      // Outstanding tab: Show File Now button at the beginning
      return [createFileNowColumn(), createActionsColumn(), ...baseColumns];
    } else if (selectedTab === 2) {
      // Completed tab: Show Edit icon at the beginning instead of File Now
      return [
        createEditColumn(),
        createPreviewColumn(),
        ...baseColumns.slice(0, -1), // Remove actions column
        createDateColumn(
          "dateFinalReportSentToClient",
          "Date Completed",
          columnWidth["dateFinalReportSentToClient"],
        ),
        createCurrencyColumn(
          "taxAmount",
          "Tax Reported",
          columnWidth["taxAmount"],
        ),
      ];
    }

    return [];
  };

  const EditToolbar = () => (
    <React.Fragment>
      <div
        style={{
          position: "fixed",
          top: "0",
          left: "0",
          zIndex: "9990",
          width: "100%",
        }}
      >
        {loading && (
          <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />
        )}
      </div>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "calc(100vh - 270px)",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          // borderRadius: "12px",
          overflow: "hidden",
          // boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
          // border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          position: "relative",
        }}
      >
        {loading && (
            <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                    backgroundColor: "rgba(250, 251, 252, 0.85)",
                    zIndex: 10,
                    border: "1px solid #e2e8f0",
                    borderRadius: 1,
                }}
            >
                <Box sx={{ width: "100%", maxWidth: 320 }}>
                    <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                    <Box sx={{ mt: 1.5, textAlign: "center" }}>
                        <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                    </Box>
                </Box>
            </Box>
        )}
        <DataGrid
          getRowId={(row) => row?.rowNum}
          rows={itemList}
          columns={getColumns()}
          pageSize={_pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          page={_currentPage - 1}
          onPageChange={(newPage) => setCurrentPage(newPage + 1)}
          pagination
          checkboxSelection={true}
          selectionModel={
            selectedRows
              ?.map((item) => item.rowNum)
              .filter(
                (rowNum): rowNum is Exclude<typeof rowNum, undefined> =>
                  rowNum !== undefined,
              ) || []
          }
          onSelectionModelChange={(newRowSelectionModel) => {
            onRowsSelectionHandler(newRowSelectionModel);
          }}
          rowCount={totalCount || 0}
          rowsPerPageOptions={[25, 50, 100]}
          paginationMode="server"
          sortingMode="server"
          sortingOrder={["desc", "asc"]}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
          style={{ textAlign: "left" }}
          components={{
            Toolbar: EditToolbar,
          }}
          sx={{
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            border: "none",
            fontSize: "14px",
            color: "#475569",
            backgroundColor: "#ffffff",
            // "& .MuiDataGrid-columnHeaderTitle": {
            //   whiteSpace: "normal",
            //   lineHeight: 1.35,
            //   fontWeight: 600,
            //   fontSize: "12px",
            //   letterSpacing: "0.05em",
            //   textTransform: "uppercase",
            //   color: "#64748b",
            // },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f8fafc",
              borderBottom: "2px solid #e2e8f0",
              borderTop: "2px solid #e2e8f0",
              minHeight: "52px !important",
              maxHeight: "52px !important",
            },
            "& .MuiDataGrid-columnHeader": {
              "&:focus, &:focus-within": { outline: "none" },
            },
            "& .MuiDataGrid-row": {
              transition: "background-color 0.15s ease",
              "&:nth-of-type(even)": {
                backgroundColor: "#fafbfc",
              },
              "&:hover": {
                backgroundColor: "#f1f5f9 !important",
              },
              "&.Mui-selected": {
                backgroundColor: "#ebf5fa !important",
                "&:hover": {
                  backgroundColor: "#dbeef7 !important",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f1f5f9",
              fontSize: "14px",
              color: "#334155",
              overflow: "visible",
              whiteSpace: "normal",
              lineHeight: "normal",
              wordWrap: "break-word",
              textOverflow: "clip",
              padding: "8px 16px",
              "&:focus": { outline: "none" },
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              marginBottom: "0px !important",
            },
            ".MuiDataGrid-virtualScroller": {
              border: "none",
            },
            "& .MuiDataGrid-main": {
              border: "none",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              minHeight: 56,
            },
            "& .MuiTablePagination-root": {
              color: "#64748b",
              fontSize: "13px",
            },
            "& .MuiTablePagination-select": {
              fontSize: "13px",
              borderRadius: "6px",
              border: "1px solid #e2e8f0",
            },
            "& .MuiIconButton-root": {
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#475569",
              },
            },
            "& .MuiCheckbox-root": {
              color: "#94a3b8",
              "&.Mui-checked": {
                color: "#2099d2",
              },
            },
          }}
          experimentalFeatures={{ newEditingApi: true }}
          columnVisibilityModel={{
            paymentDate: user?.userRoles.split(",").includes("Super Admin")
              ? true
              : false,
            paymentAmount: user?.userRoles.split(",").includes("Super Admin")
              ? true
              : false,
            lineOfBusiness: user?.userRoles.split(",").includes("Super Admin")
              ? true
              : false,
          }}
        />
      </Box>
      <AddEditStateReport
          handleClose={handleClose}
          Open={openEdit}
          StateRuleMatrixCL={formData}
          handleSubmit={handleSubmit}
          isStateReport={true}
          selectedTab={selectedTab}
      />
      <Br18Form
          handleClose={handleBrClose}
          Open={openBr18}
          br18Data={br18Data}
          br9Data={br9Data}
      />
      <StateReportFileNowDialog
          open={openFileNowDialog}
          handleClose={handleFileNowDialogClose}
          handleSubmit={handleFileNowDialogSubmit}
          selectedRow={selectedRowForFileNow}
          scheduleDataByType={scheduleDataByType}
          states={states}
      />
      {/* State Report Preview Dialog */}
      <StateReportPreviewDialog
          open={previewDialogOpen}
          onClose={() => {
            setPreviewDialogOpen(false);
          }}
          stateReport={selectedRowForFileNow}
          selectedTab={selectedTab}
          states={states}
      />
    </React.Fragment>
  );
};
